<template>
  <!-- Thumbnail preview -->
  <div
    v-if="file"
    class="aspect-[396/260] tablet-v:max-h-[483px] tablet-h:aspect-square tablet-h:max-w-[550px] tablet-h:max-h-[550px] w-full flex justify-center items-center border border-[#cccccc]/50 bg-white/20 rounded-xl mb-2.5 relative"
    :class="{ 'cursor-pointer': hasPreviewImage }"
  >
    <Player
      v-if="showMediaPlayer"
      :url="streamUrl"
      :type="file.type"
      :poster="posterUrl"
      class="p-[8px]"
      style="--player-controls-inset: 7px"
    />
    <Transition v-else name="fade" mode="out-in">
      <img
        v-if="showImage"
        :src="previewImageUrl"
        :alt="file.name"
        class="size-full object-contain p-[9px] cursor-pointer"
        @click="$emit('toggleViewer')"
      />
      <div
        v-else
        class="size-[170px] tablet-v:size-[280px] tablet-h:size-[320px]"
        @click="$emit('toggleViewer')"
      >
        <Thumbnail :type="thumbnailType" :alt="file.name" />
      </div>
    </Transition>
  </div>
  <Skeleton
    v-else
    class="aspect-[396/260] tablet-v:max-h-[483px] tablet-h:aspect-square tablet-h:max-w-[550px] tablet-h:max-h-[550px] w-full rounded-xl mb-2.5"
  />

  <!-- Action buttons -->
  <div v-if="file" class="flex items-center mb-2.5">
    <Button
      variant="stroke"
      :size="isMobile ? 'lg' : 'md'"
      :icon="ShareIcon"
      @click="$emit('share')"
    >
      Share
    </Button>
    <Button
      v-if="canDownload"
      variant="primary"
      :size="isMobile ? 'lg' : 'md'"
      :icon="DownloadIcon"
      class="ml-2"
      @click="$emit('download')"
    >
      Download
    </Button>

    <!-- Desktop: dropdown menu -->
    <div v-if="showMenu" ref="reference" class="ml-auto hidden tablet-h:block">
      <Button
        variant="stroke"
        :size="isMobile ? 'lg' : 'md'"
        :icon="MenuIcon"
        @click="menuOpen = !menuOpen"
      />
      <Teleport to="body">
        <Transition v-bind="transition">
          <div
            v-if="menuOpen && isDesktopLayout"
            ref="floating"
            class="z-50"
            :style="floatingStyles"
          >
            <FileContextMenu :file="file" @clicked="menuOpen = false" />
          </div>
        </Transition>
      </Teleport>
    </div>

    <!-- Mobile: action sheet -->
    <Button
      v-if="showMenu"
      variant="stroke"
      :size="isMobile ? 'lg' : 'md'"
      :icon="MenuIcon"
      class="ml-auto tablet-h:hidden"
      @click="$emit('threeDots')"
    />
  </div>
</template>

<script setup lang="ts">
import { Button, Thumbnail, Skeleton } from '@dacr/library'
import { ShareIcon, DownloadIcon, MenuIcon } from '@dacr/icons'
import Player from '@/features/copyright/components/Player.vue'
import FileContextMenu from '@/features/explorer/components/FileContextMenu.vue'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { FileType } from '@/shared/graphql/router/graphql'
import type { CopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { useDevice } from '@/shared/composables/useDevice'
import { useFloatingMenu } from '@/shared/composables/useFloatingMenu'
import { computed, ref, watch } from 'vue'

const props = defineProps<{
  file?: CopyrightFile
  canDownload: boolean
  showMenu: boolean
  hasPrivilegedRole: boolean
  isPrivate: boolean
}>()

defineEmits<{
  toggleViewer: []
  download: []
  share: []
  threeDots: []
}>()

const { isMobile, isTabletHorizontal, isDesktop } = useDevice()

const isDesktopLayout = computed(
  () => isTabletHorizontal.value || isDesktop.value
)

const menuOpen = ref(false)

const { reference, floating, floatingStyles, transition } = useFloatingMenu(
  menuOpen,
  {
    placement: 'bottom-end',
    matchWidth: false,
  }
)

const thumbnailType = computed(() => fileTypeToThumbnail(props.file?.type))

const hasPreviewImage = computed(() => !!props.file?.asset?.previewImageUrl)

const previewImageUrl = computed(() =>
  props.file?.asset?.previewImageUrl
    ? applyAuthorizationToken(props.file.asset.previewImageUrl)
    : undefined
)

const imageLoaded = ref(false)
const imageErrored = ref(false)
const showImage = computed(
  () => hasPreviewImage.value && imageLoaded.value && !imageErrored.value
)

const posterUrl = computed(() =>
  showImage.value ? previewImageUrl.value : undefined
)

watch(
  previewImageUrl,
  (url) => {
    imageLoaded.value = false
    imageErrored.value = false
    if (!url) return
    const image = new Image()
    image.onload = () => (imageLoaded.value = true)
    image.onerror = () => (imageErrored.value = true)
    image.src = url
  },
  { immediate: true }
)

const streamUrl = computed(() => {
  const asset = props.file?.asset
  if (asset?.__typename === 'StreamAsset') return asset.streamUrl
  return undefined
})

const showMediaPlayer = computed(() => {
  if (!props.file) return false

  const isMedia =
    props.file.type === FileType.Audio || props.file.type === FileType.Video

  if (!isMedia) return false
  if (props.hasPrivilegedRole) return true
  return !props.isPrivate
})
</script>
